// @givmo/social — each platform's small mark, beside an account's name and on a post's row
// (SMM-WEB1). Inline SVG, as the design system draws the sign-in providers' marks
// (`provider-button.tsx`): no asset pipeline, and the mark names the platform a post goes to,
// never an endorsement.

import { useId } from 'react';
import { Share2 } from 'lucide-react';
import { Icon, cn } from '@givmo/design-system';
import { platformLabel } from './social-platforms';

function FacebookMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <circle cx="12" cy="12" r="12" fill="#0866FF" />
      <path
        fill="#FFFFFF"
        d="M13.4 24v-8.5h2.6l.5-3.2h-3.1v-2c0-.9.3-1.6 1.6-1.6h1.6V5.9c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.4H7.6v3.2h2.6V24h3.2z"
      />
    </svg>
  );
}

function InstagramMark({ size }: { size: number }) {
  // One gradient per mark: a page draws several, and an id must be unique in the document.
  const gradient = `givmo-social-ig-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <defs>
        <linearGradient id={gradient} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#FEC053" />
          <stop offset="0.35" stopColor="#F2203E" />
          <stop offset="0.7" stopColor="#B729A8" />
          <stop offset="1" stopColor="#5342D6" />
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="6" fill={`url(#${gradient})`} />
      <rect
        x="5"
        y="5"
        width="14"
        height="14"
        rx="4.2"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="12" r="3.3" fill="none" stroke="#FFFFFF" strokeWidth="1.8" />
      <circle cx="16.3" cy="7.7" r="1.05" fill="#FFFFFF" />
    </svg>
  );
}

function LinkedInMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <rect width="24" height="24" rx="4" fill="#0A66C2" />
      <rect x="5.2" y="9.4" width="3" height="9.4" fill="#FFFFFF" />
      <circle cx="6.7" cy="6.4" r="1.75" fill="#FFFFFF" />
      <path
        fill="#FFFFFF"
        d="M10.4 9.4h2.9v1.3c.4-.8 1.4-1.5 2.9-1.5 3 0 3.6 2 3.6 4.5v5.1h-3v-4.5c0-1.1 0-2.4-1.5-2.4s-1.8 1.2-1.8 2.4v4.5h-3.1V9.4z"
      />
    </svg>
  );
}

/** A platform's mark, labelled for a screen reader only where `label` asks (else decorative). */
export function PlatformMark({
  platform,
  size = 20,
  label = false,
  className,
}: {
  platform: string;
  size?: number;
  label?: boolean;
  className?: string;
}) {
  const mark =
    platform === 'facebook_pages' ? (
      <FacebookMark size={size} />
    ) : platform === 'instagram' ? (
      <InstagramMark size={size} />
    ) : platform === 'linkedin' ? (
      <LinkedInMark size={size} />
    ) : (
      <span
        className="inline-flex items-center justify-center rounded-full bg-neutral-100 text-icon-muted"
        style={{ width: size, height: size }}
      >
        <Icon icon={Share2} size={Math.round(size * 0.6)} aria-hidden />
      </span>
    );
  return (
    <span
      className={cn('inline-flex shrink-0', className)}
      {...(label ? { role: 'img', 'aria-label': platformLabel(platform) } : {})}
    >
      {mark}
    </span>
  );
}
